import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Copy } from "lucide-react";
import { successfulToast, errorToast } from "../util/toastNotifications";

const HIDE_DELAY_MS = 200;

const useLinkHoverPopup = (containerRef) => {
  const [popup, setPopup] = useState({
    visible: false,
    href: "",
    top: 0,
    left: 0,
  });
  const hideTimerRef = useRef(null);
  const popupRef = useRef(null);

  const clearHide = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const hideSoon = useCallback(() => {
    clearHide();
    hideTimerRef.current = setTimeout(() => {
      setPopup((prev) => ({ ...prev, visible: false }));
    }, HIDE_DELAY_MS);
  }, [clearHide]);

  useEffect(() => () => clearHide(), [clearHide]);

  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return undefined;

    const handleMouseOver = (event) => {
      const anchor = event.target.closest?.("a");
      if (!anchor || !container.contains(anchor)) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      clearHide();
      const rect = anchor.getBoundingClientRect();
      setPopup({
        visible: true,
        href,
        top: rect.bottom + window.scrollY + 6,
        left: rect.left + window.scrollX,
      });
    };

    const handleMouseOut = (event) => {
      const anchor = event.target.closest?.("a");
      if (!anchor || !container.contains(anchor)) return;
      const related = event.relatedTarget;
      if (
        related &&
        (anchor.contains(related) || popupRef.current?.contains(related))
      ) {
        return;
      }
      hideSoon();
    };

    container.addEventListener("mouseover", handleMouseOver);
    container.addEventListener("mouseout", handleMouseOut);

    return () => {
      container.removeEventListener("mouseover", handleMouseOver);
      container.removeEventListener("mouseout", handleMouseOut);
    };
  }, [containerRef, clearHide, hideSoon]);

  useEffect(() => {
    if (!popup.visible) return undefined;
    const dismiss = () => setPopup((prev) => ({ ...prev, visible: false }));
    window.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);
    return () => {
      window.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
    };
  }, [popup.visible]);

  const handleCopy = useCallback(
    async (event) => {
      event.preventDefault();
      event.stopPropagation();
      const { href } = popup;
      if (!href) return;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(href);
        } else {
          const textarea = document.createElement("textarea");
          textarea.value = href;
          textarea.style.position = "fixed";
          textarea.style.opacity = "0";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }
        successfulToast("Link copied to clipboard");
      } catch {
        errorToast("Could not copy link");
      }
    },
    [popup]
  );

  if (!popup.visible || typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={popupRef}
      className="link-hover-popup"
      role="tooltip"
      style={{ top: popup.top, left: popup.left }}
      onMouseEnter={clearHide}
      onMouseLeave={hideSoon}
    >
      <a
        className="link-hover-popup-url"
        href={popup.href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {popup.href}
      </a>
      <button
        type="button"
        className="link-hover-popup-copy"
        onClick={handleCopy}
        title="Copy link"
        aria-label="Copy link"
      >
        <Copy size={14} />
      </button>
    </div>,
    document.body
  );
};

export default useLinkHoverPopup;
